<script setup>
import { MediaCard } from '@dashhudson/dashing-ui/media-card';
import Formatters from '@dashhudson/dashing-ui/utils/formatters';
import { camelize, camelizeKeys } from 'humps';
import cloneDeep from 'lodash/cloneDeep';
import startCase from 'lodash/startCase';
import { computed } from 'vue';

import { useMediaData } from '@/app/library/composables/useMediaData';
import { sourceTypeEnum } from '@/app/library/constants';
import { resolveMediaCardMetricValue } from '@/components/dashingUI/mediaCardMetrics';
import CollaboratorsPopover from '@/components/media/CollaboratorsPopover.vue';
import MusicMutedBadge from '@/components/media/MusicMutedBadge.vue';
import { DEFAULT_METRIC_LABELS, DEFAULT_METRICS } from '@/components/mediaCardDefaults';
import { getMetaAdsCurrency } from '@/composables/platform/metaAdsAccounts';
import { META_ADS_CHANNELS } from '@/models/dashboards/channels.enum';
import { usePlatformStore } from '@/stores/platform';

const COLLAB_AVATAR = { icon: 'users-avatar' };

const platformStore = usePlatformStore();

const props = defineProps({
  media: {
    type: Object,
    required: true,
  },
  hideMetrics: {
    type: Boolean,
    default: false,
  },
  hideSentiment: {
    type: Boolean,
    default: false,
  },
  displayMetrics: {
    type: Array,
    default: null,
  },
  metricLabels: {
    type: Object,
    default: null,
  },
  metricTypes: {
    type: Object,
    default: null,
  },
  metricTooltips: {
    type: Object,
    default: null,
  },
  highlightedMetricFieldName: {
    type: String,
    default: '',
  },
  menuItems: {
    type: Array,
    default: null,
  },
  avatar: {
    type: [String, Object],
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  caption: {
    type: String,
    default: null,
  },
  showUserDetails: {
    type: Boolean,
    default: false,
  },
  includeCaption: {
    type: Boolean,
    default: false,
  },
  // When provided, clicking the card calls this function instead of opening the media drawer.
  clickHandler: {
    type: Function,
    default: null,
  },
  // The page section the card sits in, reported when its click opens the media drawer.
  trackingSection: {
    type: String,
    default: null,
  },
  // When false, the card is non-interactive - no inner click binding (no media drawer,
  // no wrapper `click` emit, and dashing-ui skips interactive hover treatment).
  interactive: {
    type: Boolean,
    default: true,
  },
  collaborators: {
    type: Array,
    default: null,
  },
});

const emit = defineEmits(['click']);

const {
  channelKey,
  channelName,
  sourceData,
  mediaSrc,
  mediaType,
  platformMediaType,
  mediaText,
  mediaDate,
  mediaDuration,
  mediaCaption,
  mediaAvatar,
  mediaHandle,
  mediaSentiment,
  visionPrediction,
  isPromoted,
  isUGC,
  isInstagramUGC,
  isInstagramCollab,
  instagramCollaborators,
  isVisionAiGenerated,
  openMediaDrawer,
} = useMediaData(props.media);

const resolvedCollaborators = computed(() => {
  if (props.collaborators?.length) return props.collaborators;
  return isInstagramCollab.value ? instagramCollaborators.value : null;
});

const highlightedMetricField = computed(() => camelize(props.highlightedMetricFieldName));

const camelizedMedia = computed(() => camelizeKeys(props.media));

const brandId = computed(() => camelizedMedia.value?.brandId);

// Instagram connections are stored under 'facebook' in platformConnectionsMap
const platformConnectionKey = computed(() => {
  if (channelKey.value === 'instagram' || channelKey.value === 'instagram_story_frame') {
    return 'facebook';
  }
  return channelKey.value;
});

const platformConnection = computed(() =>
  camelizedMedia.value.sourceType === sourceTypeEnum.COMPETITIVE
    ? null
    : platformStore.platformConnectionsMap?.[platformConnectionKey.value]?.[brandId.value],
);

const resolvedTitle = computed(() => {
  if (!props.showUserDetails) return props.title;
  return (
    props.title ??
    mediaHandle.value ??
    platformConnection.value?.handle ??
    platformConnection.value?.email_or_handle ??
    null
  );
});

const resolvedAvatar = computed(() => {
  if (!props.showUserDetails) return props.avatar;

  if (isInstagramCollab.value) return COLLAB_AVATAR;

  const imageUrl =
    props.avatar?.image ??
    (typeof props.avatar === 'string' ? props.avatar : null) ??
    mediaAvatar.value ??
    platformConnection.value?.avatar_url ??
    null;

  if (imageUrl) {
    return { image: imageUrl };
  }

  if (camelizedMedia.value.sourceType === sourceTypeEnum.COMPETITIVE) {
    return { icon: 'user-avatar' };
  }

  // Use initials from handle as placeholder if avatar is missing
  const handle = resolvedTitle.value;
  if (handle) {
    return { label: handle };
  }

  return null;
});

const resolvedCaption = computed(() => {
  if (!props.includeCaption) return props.caption;
  return props.caption ?? mediaCaption.value ?? null;
});

const defaultDisplayMetrics = computed(() => DEFAULT_METRICS[channelKey.value] || []);

const displayMetricLabels = computed(
  () => props.metricLabels || DEFAULT_METRIC_LABELS[channelKey.value] || {},
);

const displayMetrics = computed(() => {
  if (props.hideMetrics) return [];

  const metrics = cloneDeep(
    props.displayMetrics?.length ? props.displayMetrics : defaultDisplayMetrics.value,
  ).map((field) => camelize(field));

  if (Boolean(highlightedMetricField.value) && !metrics.includes(highlightedMetricField.value)) {
    metrics.push(highlightedMetricField.value);
  }

  return metrics.map((field) => camelize(field));
});

function formatCurrency(value) {
  const formattedValue = Formatters.formatCurrency(value);

  if (META_ADS_CHANNELS.includes(channelKey.value?.toUpperCase())) {
    const currency = getMetaAdsCurrency(platformStore);
    return Formatters.formatCurrency(value, {
      includeCurrency: true,
      formatOptions: { currency },
    });
  }

  return formattedValue;
}

function formatMetricItem(metricName) {
  const { value, format } = resolveMediaCardMetricValue(sourceData.value, metricName, {
    formatCurrency,
  });

  return {
    value,
    label: displayMetricLabels.value[metricName] || startCase(metricName),
    postType: props.metricTypes?.[metricName],
    tooltip: props.metricTooltips?.[metricName],
    highlight: highlightedMetricField.value === metricName,
    ...format,
  };
}

const metricItems = computed(() => {
  if (!displayMetrics.value.length) return null;

  return displayMetrics.value.map((metricName) => formatMetricItem(metricName));
});

function onClickMedia() {
  emit('click');
  if (props.clickHandler) {
    props.clickHandler();
  } else {
    openMediaDrawer(null, {}, { section: props.trackingSection ?? undefined });
  }
}

const mediaCardListeners = computed(() => (props.interactive ? { click: onClickMedia } : {}));
</script>

<template>
  <MediaCard
    :class="{ 'cursor-pointer!': props.interactive }"
    :src="mediaSrc"
    :source-type="mediaType"
    :platform-media-type="platformMediaType"
    :video-duration="mediaDuration"
    video-preload="none"
    :video-poster="mediaSrc"
    :ugc="isUGC"
    :text="mediaText"
    :channel="channelName"
    :post-date="mediaDate"
    :promoted="isPromoted"
    :vision-prediction="visionPrediction"
    :vision-ai="isVisionAiGenerated"
    :metric-items="metricItems"
    :menu-items="props.menuItems"
    :avatar="resolvedAvatar"
    :title="resolvedTitle"
    :caption="resolvedCaption"
    :sentiment="hideSentiment ? '' : mediaSentiment"
    sentiment-readonly
    :show-footer="!hideSentiment"
    v-on="mediaCardListeners"
  >
    <template #overlay>
      <MusicMutedBadge
        v-if="isInstagramUGC"
        :mute-settings="camelizedMedia.muteSettings"
        :has-music="camelizedMedia.audioAnalysis?.hasMusic"
        :media-type="camelizedMedia.baseMediaType ?? camelizedMedia.type"
        class="absolute top-6 right-6"
      />
    </template>
    <template v-if="resolvedCollaborators?.length" #titlePopover>
      <CollaboratorsPopover :collaborators="resolvedCollaborators" />
    </template>
  </MediaCard>
</template>
